'use client';

import {
  useContext,
  useState,
  useMemo,
  type FC,
  ChangeEvent,
  ReactNode,
} from 'react';
import { ChangeLoginMethodCardProps } from './ChangePasswordCard.types';
import {
  Button,
  ButtonGroup,
  Flex,
  Text,
  Icon,
  Select,
  Card,
  Heading,
  Separator,
} from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import styles from './ChangeLoginMethodCard.module.css';
import {
  FALLBACK_SIGN_IN_METHOD,
  PREFERRED_SIGN_IN_METHOD_MAP,
  PREFERRED_SIGN_IN_METHOD_ITEMS,
} from '@/_constants/auth';
import { WithEmailFlow } from './_components/WithEmailFlow';
import { WithSMSFlow } from './_components/WithSMSFlow';
import { WithPasswordFlow } from './_components/WithPasswordFlow';
import type { PreferredSignInMethod } from '@/_types/auth';
import { getClasses } from '@/_utils/classnames';
import { TwoFactorCard } from './_components/TwoFactorCard';
import { formatPhoneNumber } from '@/_utils/phoneNumbers';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

export const ChangeLoginMethodCard: FC<ChangeLoginMethodCardProps> = () => {
  const {
    isAdvisor,
    user,
    isLoading: isUserLoading,
    setUser,
  } = useContext(UserContext);
  const { previewUser } = useContext(UserPreviewContext);

  const currentlySavedValue: PreferredSignInMethod = useMemo(
    () => user?.preferred_sign_in_method || FALLBACK_SIGN_IN_METHOD,
    [user?.preferred_sign_in_method],
  );

  const [method, setMethod] =
    useState<PreferredSignInMethod>(currentlySavedValue);
  const [isPendingUpdate, setIsPendingUpdate] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const onUpdateMethod = (
    isUpdated: boolean,
    newSignInMethod?: PreferredSignInMethod,
  ) => {
    setMethod(currentlySavedValue);
    setIsPendingUpdate(false);
    setIsEditing(false);

    if (!isUpdated || !user) return;

    setUser({
      ...user,
      preferred_sign_in_method: newSignInMethod!,
      email_verified:
        newSignInMethod === 'email' ? false : user?.email_verified,
    });
  };

  const onMethodChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMethod(event.target.value as PreferredSignInMethod);
  };

  const onUpdate = () => setIsPendingUpdate(true);

  const onToggleEdit = () => {
    setIsEditing((previous) => !previous);
    setMethod(currentlySavedValue);
    setIsPendingUpdate(false);
  };

  const getUpdateComponent = (): ReactNode => {
    switch (method) {
      case 'password':
        return <WithPasswordFlow onUpdate={onUpdateMethod} />;
      case 'sms':
        return <WithSMSFlow onUpdate={onUpdateMethod} />;
      case 'email':
        return <WithEmailFlow onUpdate={onUpdateMethod} />;
      default:
        return null;
    }
  };

  const updateComponent: ReactNode = getUpdateComponent();

  const signInMethods = useMemo(
    () =>
      PREFERRED_SIGN_IN_METHOD_ITEMS?.filter((method) => {
        const hasPhoneNumber: boolean = !!user?.phone_number;

        if (!hasPhoneNumber && method.value === 'sms') return false;

        return method;
      }),
    [user?.phone_number],
  );

  const changeLoginMethodCardClasses: string = getClasses(
    styles.changeLoginMethod__method,
    {
      [styles.changeLoginMethod__methodEditing]: isEditing,
    },
  );

  const preferredMethod: string = useMemo(() => {
    switch (user?.preferred_sign_in_method) {
      case 'email':
        return user?.email || 'email is not available';
      case 'sms':
        return (
          formatPhoneNumber(user?.phone_number) ||
          'phone number is not available'
        );
      case 'password':
        return 'secure password';
      default:
        return user?.email || 'missing email address';
    }
  }, [user?.preferred_sign_in_method, user?.email, user?.phone_number]);

  const isPreview: boolean =
    isAdvisor && !!previewUser?.id && previewUser?.id !== user?.id;

  if (!user?.email) {
    return (
      <Card padding={12} gap={8}>
        <Text type="small">
          Email address is required for password sign-ins.
        </Text>
      </Card>
    );
  }

  return (
    <Card padding={12} gap={8}>
      <Heading level={4}>Sign-in method</Heading>
      <Separator />
      <Flex isColumn={true} gap={4}>
        <Flex
          gap={8}
          isYCentered={true}
          isBetween={true}
          className={changeLoginMethodCardClasses}
        >
          {isEditing ? (
            <Select
              onChange={onMethodChange}
              isDisabled={isUserLoading || isPendingUpdate}
              isLabelHidden={true}
              selectedValue={method}
              label="Select sign-in method"
              items={signInMethods}
            />
          ) : (
            <Flex isYCentered={true} gap={6}>
              <Flex
                isCentered={true}
                className={styles.changeLoginMethod__methodTile}
              >
                <Icon name="lock" size={24} />
              </Flex>
              <Flex isColumn={true}>
                <Text type="small" fontWeight="medium" color="currentcolor">
                  {PREFERRED_SIGN_IN_METHOD_MAP[currentlySavedValue]}
                </Text>
                <Text type="tiny" fontWeight="medium" color="secondary">
                  {preferredMethod}
                </Text>
              </Flex>
            </Flex>
          )}
          <ButtonGroup
            className={styles.buttonGroup}
            align="right"
            isSameLine={true}
          >
            <Button
              isDisabled={isUserLoading || isPreview}
              intent="secondary"
              onClick={onToggleEdit}
              size="small"
              value={isEditing ? 'Cancel' : 'Change'}
            />
            {isEditing && (
              <Button
                isDisabled={
                  isUserLoading ||
                  isPendingUpdate ||
                  (currentlySavedValue !== 'password' &&
                    currentlySavedValue === method)
                }
                intent="primary"
                size="small"
                onClick={onUpdate}
                value="Update"
              />
            )}
          </ButtonGroup>
        </Flex>
        {isPendingUpdate && (
          <Flex
            isColumn={true}
            padding={12}
            className={styles.changeLoginMethod__form}
          >
            {updateComponent}
          </Flex>
        )}
      </Flex>
      <Separator isLight={true} />
      <TwoFactorCard />
    </Card>
  );
};
